import React, { useState, useEffect } from "react";
import { Modal, Button, List, Typography, Divider } from "antd";
import {
  CheckCircleOutlined,
  BankOutlined,
  DollarOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import NumberFormat from "react-number-format";
import { useNavigate } from "react-router-dom";
import { _getKycRequirements } from "../../common/axios_services";
import AppRoutes from "../../common/routes";

const { Title, Text } = Typography;

const TierUpgradeModal = ({
  visible,
  onCancel,
  onUpgrade,
  walletBalance,
  loading,
}) => {
  const benefits = [
    {
      icon: <BankOutlined className="text-blue-500" />,
      title: "Dedicated NUBAN",
      description: "Secured wallet with a dedicated NUBAN account",
    },
    {
      icon: <DollarOutlined className="text-green-500" />,
      title: "50% Cash Discounts",
      description: "Up to 50% cash discounts on everything you buy",
    },
    {
      icon: <GiftOutlined className="text-purple-500" />,
      title: "Weekly Cashback",
      description: "Earn more weekly cashback compared to Tier 1",
    },
  ];

  const navigate = useNavigate();
  const [requirements, setRequirements] = useState(null);

  useEffect(() => {
    if (visible) {
      _getKycRequirements()
        .then((res) => {
          const reqData = res?.data?.data || res?.data;
          if (reqData && typeof reqData === "object") {
            setRequirements(reqData);
          }
        })
        .catch(() => {});
    }
  }, [visible]);

  const upgradeFee = requirements?.upgradeFee ?? 100;
  const minBalance = requirements?.minBalance ?? 500;
  const effectiveBalance =
    typeof walletBalance === "number"
      ? walletBalance
      : requirements?.spendableBalance ?? 0;
  const hasEnoughBalance = effectiveBalance >= minBalance + upgradeFee;

  return (
    <Modal
      title={
        <div className="text-center">
          <Title level={3} className="mb-2">
            Tier 2 Benefits
          </Title>
          <Text className="text-gray-600">Unlock premium wallet features</Text>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={500}
      centered
      bodyStyle={{
        maxHeight: "70vh",
        overflowY: "auto",
        padding: "16px",
      }}
    >
      <div className="py-4">
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <Title level={4} className="mb-3 text-blue-800">
            Tier 2 Benefits
          </Title>
          <List
            dataSource={benefits}
            renderItem={(item) => (
              <List.Item className="px-0">
                <List.Item.Meta
                  avatar={item.icon}
                  title={<Text strong>{item.title}</Text>}
                  description={item.description}
                />
              </List.Item>
            )}
          />
        </div>

        <Divider />

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Text>Minimum Balance Required:</Text>
            <Text>₦{minBalance}</Text>
          </div>
          <div className="flex justify-between items-center">
            <Text>Current Balance:</Text>
            <Text strong className="">
              ₦
              <NumberFormat
                thousandSeparator={true}
                displayType="text"
                value={walletBalance || 0}
              />
            </Text>
          </div>

          <div className="flex justify-between items-center">
            <Text>Upgrade Fee:</Text>
            <Text>₦{upgradeFee}</Text>
          </div>

          <Divider />

          <div className="flex justify-between items-center">
            <Text strong>Wallet Balance:</Text>
            <Text strong className="">
              ₦
              <NumberFormat
                thousandSeparator={true}
                displayType="text"
                value={walletBalance - upgradeFee || 0}
              />
            </Text>
          </div>

          {!hasEnoughBalance && (
            <div className="bg-red-50 p-4 rounded-xl border border-red-200 space-y-3">
              <Text className="text-red-600 block">
                Insufficient balance. You need ₦
                {(minBalance + upgradeFee - effectiveBalance).toLocaleString()} more to upgrade.
              </Text>
              <Button
                type="primary"
                className="bg-green-600 hover:bg-green-700 border-none w-full font-semibold"
                onClick={() => {
                  onCancel();
                  navigate(AppRoutes.wallet || "/wallet", {
                    state: { openFundWallet: true },
                  });
                }}
              >
                Fund Wallet Now
              </Button>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <Button block onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            block
            onClick={onUpgrade}
            loading={loading}
            disabled={!hasEnoughBalance}
          >
            Upgrade Now
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TierUpgradeModal;
